import { create } from 'zustand'
import { API_BASE_URL, STORAGE_KEYS } from '@/lib/config'
import { clearSessionCallerId } from '@/lib/sessionCallerId'

export interface User {
  id?: number
  email?: string
  firstName?: string
  lastName?: string
  role?: 'AGENT' | 'MANAGER' | string
  language?: string
  companyId?: number
  companyName?: string
}

interface AuthState {
  accessToken: string | null
  refreshToken: string | null
  user: User | null
  isAuthenticated: () => boolean
  setTokens: (access: string, refresh: string) => void
  setUser: (user: User) => void
  clear: () => void
}

const LEGACY_KEYS = [
  STORAGE_KEYS.ACCESS_TOKEN,
  STORAGE_KEYS.REFRESH_TOKEN,
  STORAGE_KEYS.USER_ID,
  STORAGE_KEYS.USER_EMAIL,
  STORAGE_KEYS.USER_FIRST_NAME,
  STORAGE_KEYS.USER_LAST_NAME,
  STORAGE_KEYS.USER_ROLE,
  STORAGE_KEYS.COMPANY_ID,
  STORAGE_KEYS.COMPANY_NAME,
  'callizy_access_token',
  'callizy_session_caller_id',
]

function isTokenFresh(token: string | null, minMs = 60_000): boolean {
  if (!token) return false
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    return typeof payload.exp === 'number' && payload.exp * 1000 - Date.now() > minMs
  } catch {
    return false
  }
}

export const useAuthStore = create<AuthState>()((set, get) => ({
  accessToken: typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) : null,
  refreshToken: typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) : null,
  user: null,

  isAuthenticated: () => isTokenFresh(get().accessToken),

  setTokens: (access, refresh) => {
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, access)
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refresh)
    set({ accessToken: access, refreshToken: refresh })
  },

  setUser: (user) => {
    if (user.firstName !== undefined) localStorage.setItem(STORAGE_KEYS.USER_FIRST_NAME, user.firstName ?? '')
    if (user.lastName !== undefined) localStorage.setItem(STORAGE_KEYS.USER_LAST_NAME, user.lastName ?? '')
    if (user.role !== undefined) localStorage.setItem(STORAGE_KEYS.USER_ROLE, user.role ?? '')
    if (user.companyId !== undefined) localStorage.setItem(STORAGE_KEYS.COMPANY_ID, user.companyId?.toString() ?? '')
    if (user.companyName !== undefined) localStorage.setItem(STORAGE_KEYS.COMPANY_NAME, user.companyName ?? '')
    set({ user })
  },

  clear: () => {
    // Fire-and-forget : invalide le refresh token côté serveur (blacklist
    // dj_rest_auth). Évite qu'un refresh token sniffé reste valide après
    // logout / changement d'utilisateur. On utilise fetch direct pour éviter
    // un import circulaire avec lib/api (qui dépend de ce store).
    const refresh = get().refreshToken
    if (refresh) {
      void fetch(`${API_BASE_URL}/api/auth/logout/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh }),
      }).catch(() => {
        // Best-effort : ignore network / token déjà invalide / etc.
      })
    }
    for (const key of LEGACY_KEYS) localStorage.removeItem(key)
    clearSessionCallerId()   // sessionStorage : la ligne choisie ne passe pas au compte suivant
    document.cookie = 'sessionid=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'
    document.cookie = 'csrftoken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'
    set({ accessToken: null, refreshToken: null, user: null })
  },
}))

export function getAccessToken(): string | null {
  return useAuthStore.getState().accessToken
}
